'use client';

import { useUser } from '@auth0/nextjs-auth0';
import { useRouter } from 'next/navigation';
import React, { useState } from 'react';
import styles from '../styles/Home.module.css';
import { SignInButton } from '@/lib/SignInButton';

const DEFAULT_NOTES = `# VISITOR INTERVIEW — Vera

## Your role
You're the sole interviewer. A visitor came to this site on purpose to
talk with you. Your job: make them feel a sharp interviewer asked them
three good questions, and they leave with something they didn't have
when they arrived.

## Tone
- Warm but sharp. Curious, not gushing.
- Treat them as if they have something interesting to say.
- No flattery. No "great question" or "love that." Respond to substance.
- 2-4 sentences per turn. Pace measured.

## Flow

### Open (~15s)
> "Welcome. I'm Vera — an AI built to run interviews on this site.
> I'll ask you three questions. Tell me first — what are you working
> on right now? Not the official version, the version you'd describe
> to a friend."

### Question 1 — The work (concrete, ask after their intro)
Pick one specific from what they said. Don't accept abstract.
- "What's the part you're stuck on right now?"
- "Why this and not [obvious adjacent thing]?"
- One follow-up max.

### Question 2 — Contrarian belief
> "What do you believe about your work that most people in your field
> would disagree with?"

If they hedge:
> "Pick one thing — even small — where you think the consensus is wrong."

Don't validate or refute. Acknowledge the position cleanly.

### Question 3 — Recent lived insight
> "What's something you understand now that you wouldn't have understood
> twelve months ago?"

Let silence sit if they think.

### Close (~15s)
Synthesize ONE thing they said into a clean sentence — show you listened.
Then a thought to take with them.

> "What stuck with me — [paraphrase their best insight in one line].
> Worth coming back to in twelve months. Thanks for the time."

## Handling different visitor types

- **Founder / operator:** stay on substance, no startup-cliché praise
- **Designer / creative:** anchor questions still work — adapt language
- **Student / early career:** treat them seriously, don't simplify
- **Skeptic ("you're a bot"):** confirm yes, you're AI, move on
- **Pitch-mode:** redirect — "Save the pitch. What's the part you're
  uncertain about?"

## What to avoid
- Don't list multiple questions at once
- Don't summarize them back ("so you're saying...")
- Don't pitch the podcast or site
- Don't ask permission ("is it OK if I ask...")
- Don't end on flattery — end on insight
- Don't claim to be human. You're openly AI.`;

type StartResponse = {
  roomName: string;
  hostPath: string;
  guestPath: string;
  dispatchId: string;
  egressId: string | null;
  recordingPath: string | null;
};

type EngineArch = 'realtime' | 'pipeline';
type VoiceProvider = 'openai' | 'cartesia' | 'elevenlabs';

const ENGINES: {
  value: string;
  label: string;
  arch: EngineArch;
  voiceProvider: VoiceProvider;
}[] = [
  {
    value: 'vera-lite',
    label: 'Vera Lite — fast, cheap (gpt-4o-mini-realtime)',
    arch: 'realtime',
    voiceProvider: 'openai',
  },
  {
    value: 'vera-pro',
    label: 'Vera Pro — premium realtime (gpt-realtime)',
    arch: 'realtime',
    voiceProvider: 'openai',
  },
  {
    value: 'vera-cartesia',
    label: 'Vera Cartesia — Pipeline + Sonic 2 voices ⭐',
    arch: 'pipeline',
    voiceProvider: 'cartesia',
  },
  {
    value: 'vera-eleven',
    label: 'Vera ElevenLabs — Pipeline (needs ElevenLabs Creator+ key)',
    arch: 'pipeline',
    voiceProvider: 'elevenlabs',
  },
];

// OpenAI Realtime voices (used when engine.arch === 'realtime').
// `marin` and `cedar` are gpt-realtime-only — flagged so we can warn on Lite.
const OPENAI_VOICES = [
  { value: 'shimmer', label: 'Shimmer — warm female (default Lite)', sample: '/voice-samples/shimmer.mp3', proOnly: false },
  { value: 'sage', label: 'Sage — calm, contemplative female', sample: '/voice-samples/sage.mp3', proOnly: false },
  { value: 'coral', label: 'Coral — energetic, bright female', sample: '/voice-samples/coral.mp3', proOnly: false },
  { value: 'nova', label: 'Nova — smooth, neutral female', sample: '/voice-samples/nova.mp3', proOnly: false },
  { value: 'alloy', label: 'Alloy — neutral, AI-assistant', sample: '/voice-samples/alloy.mp3', proOnly: false },
  { value: 'ash', label: 'Ash — warm male', sample: '/voice-samples/ash.mp3', proOnly: false },
  { value: 'ballad', label: 'Ballad — soft, expressive male', sample: '/voice-samples/ballad.mp3', proOnly: false },
  { value: 'echo', label: 'Echo — deeper, calm male', sample: '/voice-samples/echo.mp3', proOnly: false },
  { value: 'verse', label: 'Verse — energetic, expressive', sample: '/voice-samples/verse.mp3', proOnly: false },
  { value: 'marin', label: 'Marin — newest, expressive female (Pro only)', sample: '/voice-samples/marin.mp3', proOnly: true },
  { value: 'cedar', label: 'Cedar — newest, expressive male (Pro only)', sample: '/voice-samples/cedar.mp3', proOnly: true },
];

// Cartesia voices (Sonic 2). Default Pipeline TTS — works on free tier.
const CARTESIA_VOICES = [
  { value: '2a17e905-8f14-4db7-9b9d-9223a8e3f278', label: 'Jane — Digital Guide (crisp, modern, intelligent — default)', sample: '/voice-samples/cartesia/jane.mp3' },
  { value: 'f0377496-2708-4cc9-b2f8-1b7fdb5e1a2a', label: 'Elaine — Confident Guide (assured, calm)', sample: '/voice-samples/cartesia/elaine.mp3' },
  { value: 'cec7cae1-ac8b-4a59-9eac-ec48366f37ae', label: 'Haley — Engaging Friend (casual, friendly)', sample: '/voice-samples/cartesia/haley.mp3' },
  { value: 'd6905573-8e91-4e32-b103-fd4d1205cd87', label: 'Mindy — Spirited Ally (enthusiastic, bright)', sample: '/voice-samples/cartesia/mindy.mp3' },
  { value: 'e4d5f4c4-6601-4779-bee1-b3c14d629dc6', label: 'Jillian — Happy Spirit (cheerful, warm)', sample: '/voice-samples/cartesia/jillian.mp3' },
  { value: 'e5a6cd18-d552-4192-9533-82a08cac8f23', label: 'Patricia — Veteran Support (matured, warm)', sample: '/voice-samples/cartesia/patricia.mp3' },
  { value: 'd709a7e8-9495-4247-aef0-01b3207d11bf', label: 'Donny — Steady Presence (neutral male)', sample: '/voice-samples/cartesia/donny.mp3' },
  { value: 'dbfa416f-d5c3-4006-854b-235ef6bdf4fd', label: 'Damon — Commanding Narrator (deep, gravitas)', sample: '/voice-samples/cartesia/damon.mp3' },
  { value: 'f688c0a6-dddd-48ba-8246-c099d494a162', label: 'Romeo — Calm Narrator (smooth, calm)', sample: '/voice-samples/cartesia/romeo.mp3' },
];

// ElevenLabs voices (used when engine.voiceProvider === 'elevenlabs').
// Free/Starter tiers do not support WebSocket streaming — Creator+ needed.
const ELEVEN_VOICES = [
  { value: 'EXAVITQu4vr4xnSDxMaL', label: 'Sarah — professional female (default)', sample: '/voice-samples/eleven/sarah.mp3' },
  { value: 'Xb7hH8MSUJpSbSDYk0k2', label: 'Alice — clear, engaging educator (British)', sample: '/voice-samples/eleven/alice.mp3' },
  { value: 'XrExE9yKIg1WjnnlVkGX', label: 'Matilda — knowledgable, upbeat female', sample: '/voice-samples/eleven/matilda.mp3' },
  { value: 'hpp4J3VqNfWAUOO0d1Us', label: 'Bella — bright, professional female', sample: '/voice-samples/eleven/bella.mp3' },
  { value: 'cgSgspJ2msm6clMCkdW9', label: 'Jessica — playful, warm female', sample: '/voice-samples/eleven/jessica.mp3' },
  { value: 'pFZP5JQG7iQjIQuC4Bku', label: 'Lily — velvety, British female', sample: '/voice-samples/eleven/lily.mp3' },
  { value: 'JBFqnCBsd6RMkjVDRZzb', label: 'George — warm storyteller (British male)', sample: '/voice-samples/eleven/george.mp3' },
  { value: 'SAz9YHcvj6GT2YYXdXww', label: 'River — relaxed, neutral', sample: '/voice-samples/eleven/river.mp3' },
  { value: 'cjVigY5qzO86Huf0OWal', label: 'Eric — smooth, trustworthy male', sample: '/voice-samples/eleven/eric.mp3' },
];

export default function Page() {
  const router = useRouter();
  const { user } = useUser();
  const [episodeId, setEpisodeId] = useState('Episode 1');
  const [engine, setEngine] = useState('vera-lite');
  const [voice, setVoice] = useState('shimmer');
  const [notes, setNotes] = useState(DEFAULT_NOTES);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<string | null>(null);
  const previewRef = React.useRef<HTMLAudioElement | null>(null);

  // Active voice catalog depends on engine.voiceProvider:
  // openai → OpenAI Realtime catalog
  // cartesia → Cartesia Sonic 2 catalog
  // elevenlabs → ElevenLabs catalog
  const currentEngine = ENGINES.find((e) => e.value === engine) ?? ENGINES[0];
  const voiceCatalog =
    currentEngine.voiceProvider === 'cartesia'
      ? CARTESIA_VOICES
      : currentEngine.voiceProvider === 'elevenlabs'
      ? ELEVEN_VOICES
      : OPENAI_VOICES;

  // When engine changes, reset voice to the first option in the new catalog
  // (so we never end up with a stale OpenAI voice while on the ElevenLabs
  // pipeline, or vice versa).
  React.useEffect(() => {
    if (!voiceCatalog.find((v) => v.value === voice)) {
      setVoice(voiceCatalog[0].value);
    }
  }, [engine, voiceCatalog, voice]);

  const currentVoice = voiceCatalog.find((v) => v.value === voice) ?? voiceCatalog[0];

  const playVoiceSample = () => {
    if (previewRef.current) {
      previewRef.current.pause();
      previewRef.current = null;
    }
    if (previewing === voice) {
      setPreviewing(null);
      return;
    }
    const audio = new Audio(currentVoice.sample);
    audio.onended = () => setPreviewing(null);
    audio.onerror = () => setPreviewing(null);
    audio.play().catch(() => setPreviewing(null));
    previewRef.current = audio;
    setPreviewing(voice);
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await fetch('/api/start-episode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ episodeId, engine, voice, notes }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `${res.status} ${res.statusText}`);
      }
      const data = (await res.json()) as StartResponse;
      // Skip the intermediate "dispatched" screen — go straight into the
      // pre-join flow. The share-with-guest URL is rendered inside the
      // pre-join view (see app/rooms/[roomName]/PageClientImpl.tsx).
      router.push(data.hostPath);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
      setBusy(false);
    }
  };

  return (
    <main className={styles.main} data-lk-theme="default">
      <div style={{ maxWidth: 640, width: '100%' }}>
        <header style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <h1 style={{ fontSize: 28, marginBottom: 6 }}>AI POD — Studio</h1>
            <p style={{ opacity: 0.7, fontSize: 14, margin: 0 }}>
              Brief Vera, then open the room. Host + guest + Vera join the same call.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {user ? (
              <>
                <a
                  href="/recordings"
                  className="lk-button"
                  style={{ textDecoration: 'none', whiteSpace: 'nowrap', fontSize: 13 }}
                >
                  📁 Recordings
                </a>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '4px 10px 4px 4px',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 999,
                    fontSize: 12,
                  }}
                >
                  {user.picture && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={user.picture}
                      alt=""
                      style={{ width: 24, height: 24, borderRadius: '50%' }}
                    />
                  )}
                  <span style={{ opacity: 0.85 }}>{user.name ?? user.email ?? 'You'}</span>
                  <a
                    href="/auth/logout"
                    style={{
                      color: 'rgba(255, 122, 122, 0.9)',
                      textDecoration: 'none',
                      marginLeft: 4,
                      fontSize: 11,
                      opacity: 0.8,
                    }}
                  >
                    Sign out
                  </a>
                </div>
              </>
            ) : (
              <SignInButton variant="primary" />
            )}
          </div>
        </header>

        {!user && (
          <div
            style={{
              background: 'rgba(80, 130, 250, 0.10)',
              border: '1px solid rgba(80, 130, 250, 0.35)',
              padding: '10px 14px',
              borderRadius: 8,
              fontSize: 13,
              marginBottom: 18,
              color: '#b8c8ff',
            }}
          >
            👋 You&apos;re in trial mode — try the studio with Vera, no signup needed.
            To <strong>save recordings</strong> to your library, sign in with SRG+ (top-right).
          </div>
        )}

        <form
          onSubmit={onSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, opacity: 0.8 }}>Title</span>
              <input
                type="text"
                value={episodeId}
                onChange={(e) => setEpisodeId(e.target.value)}
                pattern="[A-Za-z0-9 _-]{1,40}"
                required
                placeholder="Episode 1"
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, opacity: 0.8 }}>Engine</span>
              <select
                value={engine}
                onChange={(e) => setEngine(e.target.value)}
                style={{
                  padding: '0.5em 0.75em',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 6,
                  color: 'inherit',
                  fontSize: 14,
                }}
              >
                {ENGINES.map((e) => (
                  <option key={e.value} value={e.value}>
                    {e.label}
                  </option>
                ))}
              </select>
              <span style={{ fontSize: 12, opacity: 0.55 }}>
                Internal testing — no plan locks. Lite is ~12× cheaper than Pro.
              </span>
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, opacity: 0.8 }}>Voice</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  value={voice}
                  onChange={(e) => setVoice(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '0.5em 0.75em',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 6,
                    color: 'inherit',
                    fontSize: 14,
                  }}
                >
                  {voiceCatalog.map((v) => (
                    <option key={v.value} value={v.value}>
                      {v.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={playVoiceSample}
                  className="lk-button"
                  title={previewing === voice ? 'Stop preview' : 'Play sample'}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {previewing === voice ? '⏸ Stop' : '▶ Listen'}
                </button>
              </div>
              {currentEngine.arch === 'realtime' &&
                Boolean((currentVoice as { proOnly?: boolean }).proOnly) &&
                engine === 'vera-lite' && (
                  <span style={{ fontSize: 12, color: '#ffb4b4' }}>
                    ⚠ This voice is only available on Vera Pro engine — switch engine or pick another voice.
                  </span>
                )}
              {currentEngine.voiceProvider === 'cartesia' && (
                <span style={{ fontSize: 12, opacity: 0.55 }}>
                  Pipeline: OpenAI STT → GPT-4o-mini → Cartesia Sonic 2.
                  ~500ms latency, expressive natural voice.
                </span>
              )}
              {currentEngine.voiceProvider === 'elevenlabs' && (
                <span style={{ fontSize: 12, color: '#ffb4b4' }}>
                  ⚠ ElevenLabs WebSocket streaming requires Creator+ subscription ($22/mo).
                  Free/Starter return silent audio. Use Cartesia for now.
                </span>
              )}
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, opacity: 0.8 }}>
                Producer notes — what Vera should do this episode
              </span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={20}
                style={{
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: 13,
                  lineHeight: 1.5,
                  padding: 10,
                }}
              />
              <span style={{ fontSize: 12, opacity: 0.55 }}>
                These notes go straight into Vera&apos;s instructions for this recording.
                Plain English. Be specific.
              </span>
            </label>

            {error && (
              <div
                style={{
                  background: 'rgba(255, 80, 80, 0.12)',
                  border: '1px solid rgba(255, 80, 80, 0.4)',
                  padding: 10,
                  borderRadius: 6,
                  fontSize: 13,
                  color: '#ffb4b4',
                }}
              >
                {error}
              </div>
            )}

            <button type="submit" className="lk-button" disabled={busy} style={{ marginTop: 8 }}>
              {busy ? 'Starting…' : '▶ Start Episode'}
            </button>
          </form>
      </div>
    </main>
  );
}
